import React from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import gtmTracker from '@utils/gtmTracker';

import { useModals } from '@shared/contexts';

import useEnglishTranslation from '@hooks/use-english-translation';

import { PROMOTION_STATUS, PromotionDetailedType } from '@types';

import { Button } from '@components/button';
import { PromotionDate } from '@components/promotion-date';

import { Timer } from './components/timer';
import * as Styled from './promotion-banner.styled';
import { ModalName } from '../modals';

type OwnProps = {
	promo: PromotionDetailedType;
	isDetailed?: boolean;
	isParticipating: boolean;
};

const PromotionBanner: React.FC<OwnProps> = ({ promo, isParticipating, isDetailed }) => {
	const { t } = useTranslation('promotions');
	const tEn = useEnglishTranslation(['promotions', 'main']);
	const navigate = useNavigate();
	const { openModal } = useModals();

	const handleButtonClick = (id: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Promotions',
			event_action: 'click',
			event_label:
				isParticipating || promo.status === PROMOTION_STATUS.ACTIVE ? tEn('tickets_buy') : tEn('main:readMoreBtn')
		});
		navigate('/promotion', { state: { promotionId: id } });
	};

	const handleTicketsBuy = () => {
		gtmTracker.trackEvent('click', {
			event_category: 'Promotions',
			event_action: 'click',
			event_label: tEn('tickets_buy')
		});
		openModal(ModalName.PROMOTION_TICKETS, { ticket: promo?.ticket });
	};

	const handleStatus = () => {
		switch (promo.status) {
			case PROMOTION_STATUS.ACTIVE:
				return t('chip_started');
			case PROMOTION_STATUS.PENDING:
				return t('chip_new');
			default:
				return 'Err';
		}
	};

	return (
		<Styled.Wrapper $bg={promo.image}>
			<Styled.Content>
				<Styled.DateRow>
					<Styled.ParticipationChip $isActive={isParticipating}>
						{isParticipating ? t('chip_participating') : handleStatus()}
					</Styled.ParticipationChip>
					<PromotionDate isActive={isParticipating} start_date={promo.start_date} end_date={promo.end_date} />
				</Styled.DateRow>
				<Styled.TextWrapper>
					<Styled.Title>{promo.name}</Styled.Title>
					<Styled.Description>{promo.description}</Styled.Description>
				</Styled.TextWrapper>
				<Styled.FooterWrapper>
					{promo.status === PROMOTION_STATUS.PENDING && (
						<Styled.TimerWrapper>
							<Styled.TimerTitle>{t('to_promo_start')}:</Styled.TimerTitle>
							<Timer time={promo.start_date} />
						</Styled.TimerWrapper>
					)}
					{promo.status === PROMOTION_STATUS.ACTIVE && isParticipating && (
						<Styled.TimerWrapper>
							<Styled.TimerTitle>{t('to_promo_end')}:</Styled.TimerTitle>
							<Timer time={promo.end_date} />
						</Styled.TimerWrapper>
					)}
					{isDetailed ? (
						promo.status === PROMOTION_STATUS.ACTIVE && (
							<Styled.ButtonWrapper>
								<Button onClick={handleTicketsBuy} color='primary' size='large'>
									{t('tickets_buy')}
								</Button>
							</Styled.ButtonWrapper>
						)
					) : (
						<Styled.ButtonWrapper>
							<Button
								onClick={() => handleButtonClick(promo.id)}
								size='large'
								color={isParticipating || promo.status === PROMOTION_STATUS.ACTIVE ? 'primary' : 'dark'}
							>
								{isParticipating || promo.status === PROMOTION_STATUS.ACTIVE ? t('tickets_buy') : t('main:readMoreBtn')}
							</Button>
						</Styled.ButtonWrapper>
					)}
				</Styled.FooterWrapper>
			</Styled.Content>
		</Styled.Wrapper>
	);
};

export default PromotionBanner;
